import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/** Convert Latin (0-9) digits to Eastern Arabic-Indic digits (٠-٩).
 *  Only touches digit characters — punctuation, letters, commas, etc.
 *  pass through unchanged. Safe to run on any string. */
export const arDigits = (s) =>
  s == null ? "" : String(s).replace(/(\d)\.(\d)/g, "$1٫$2").replace(/[0-9]/g, (d) => "٠١٢٣٤٥٦٧٨٩"[+d]);

/** Normalize Eastern Arabic-Indic (٠-٩) and Persian (۰-۹) digits, and the
 *  Arabic decimal separator (٫), to their Latin ASCII equivalents. Lets
 *  Arabic-keyboard users type numbers into numeric inputs. */
export const normalizeDigits = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .replace(/\u066B/g, ".") // Arabic decimal separator → .
    .replace(/\u066C/g, ""); // Arabic thousands sep in typed input → drop
};

/** Post-format sweep: swap Arabic thousands separator (U+066C, visually a small
 *  elevated tick) for a normal baseline comma. Applied to any Arabic-locale
 *  number string so results read cleanly. */
export const fixArThousands = (s) => (s == null ? "" : String(s).replace(/\u066C/g, ","));

/** Number → Arabic-locale-formatted string (with Arabic-Indic digits and a
 *  baseline comma thousands separator). Falls back to Latin locale when
 *  `isAR` is false. */
export const fmtLocaleNum = (n, isAR) => {
  if (n == null) return "—";
  if (!isAR) return Number(n).toLocaleString();
  return fixArThousands(Number(n).toLocaleString("ar-EG"));
};

/** AED currency in the visitor's language.
 *  - `mode: "full"` → integer with thousands (AED 705,000,000 / ٧٠٥,٠٠٠,٠٠٠ د.إ)
 *  - `mode: "bn"`   → billions with 2 decimals (AED 43.51B / ٤٣٫٥١ مليار د.إ) */
export const fmtAED = (n, isAR, mode = "full") => {
  if (n == null) return "—";
  if (mode === "bn") {
    const num = Number(n).toFixed(2);
    return isAR ? `${arDigits(num)} مليار د.إ` : `AED ${num}B`;
  }
  const rounded = Math.round(Number(n));
  const localeNum = isAR ? fixArThousands(rounded.toLocaleString("ar-EG")) : rounded.toLocaleString();
  return isAR ? `${localeNum} د.إ` : `AED ${localeNum}`;
};

/** Arabic label for an ISO-week string like "2026-W36" → "أسبوع ٣٦ · ٢٠٢٦".
 *  In English mode it just returns the input unchanged. */
export const fmtIsoWeekLabel = (iso, isAR) => {
  if (!iso) return "";
  if (!isAR) return iso; // "2026-W36"
  const m = /^(\d{4})-W(\d{2})$/.exec(iso);
  if (!m) return arDigits(iso);
  return `أسبوع ${arDigits(m[2])} · ${arDigits(m[1])}`;
};

/** Format an ISO date "2026-09-06" in the visitor's language. */
export const fmtIsoDate = (s, isAR) => (isAR ? arDigits(s) : s);

// Arabic count phrase with singular / dual / plural (3–10) / 11+ forms.
export function arCount(n, { one, two, few, many }) {
  if (n === 1) return one;
  if (n === 2) return two;
  const r = Math.abs(n) % 100;
  return `${arDigits(n)} ${r >= 3 && r <= 10 ? few : many}`;
}

